// Outbound webhook for AlertEvents. Fire-and-forget HTTPS POST to a
// user-configured endpoint. Demo feature — no retry, no queue, no batching.

import { eventToJsonRecord, type AlertEvent } from "../inference/types";

// Sealog ingestion endpoint — used as a fallback when the user hasn't
// configured their own webhook URL/key. Lets the demo just work out of the
// box; anyone with their own backend can override via the sidebar inputs.
export const FALLBACK_WEBHOOK_URL =
  "https://nniptkusutlbhsnyzqoj.supabase.co/functions/v1/ingest-safety-event";
export const FALLBACK_WEBHOOK_API_KEY = "sealog-cam-demo-2026";

export interface WebhookConfig {
  enabled: boolean;
  url: string;
  apiKey: string;
}

/** Apply fallback URL/key when the user hasn't entered their own. */
function resolveConfig(cfg: WebhookConfig): WebhookConfig {
  return {
    enabled: cfg.enabled,
    url: cfg.url.trim() === "" ? FALLBACK_WEBHOOK_URL : cfg.url,
    apiKey: cfg.apiKey === "" ? FALLBACK_WEBHOOK_API_KEY : cfg.apiKey,
  };
}

export interface WebhookResult {
  ok: boolean;
  status?: number;
  error?: string;
}

export async function sendEventWebhook(
  cfg: WebhookConfig,
  ev: AlertEvent,
  snapshot?: Blob,
): Promise<WebhookResult> {
  if (!cfg.enabled) return { ok: false, error: "disabled" };
  const resolved = resolveConfig(cfg);

  const payload = eventToJsonRecord(ev);
  // `snapshot_path` only makes sense inside the local IndexedDB + zip export.
  // For the outbound payload, embed the JPEG as a data URL so the receiver
  // gets everything in one request.
  delete payload["snapshot_path"];
  if (snapshot) {
    payload["snapshot_data_url"] = await blobToDataUrl(snapshot);
  }

  return postJson(resolved, payload);
}

/** Send a synthetic event to let the user verify their endpoint. */
export async function sendTestWebhook(cfg: WebhookConfig): Promise<WebhookResult> {
  const resolved = resolveConfig({ ...cfg, enabled: true });
  const payload = {
    event_id: "evt_test",
    type: "webhook_test",
    severity: "info" as const,
    track_id: null,
    class: "test",
    confidence: 1,
    seen_for_seconds: 0,
    reason: "Test event from object-detection prototype",
    timestamp: new Date().toISOString(),
  };
  return postJson(resolved, payload);
}

async function postJson(
  cfg: WebhookConfig,
  body: Record<string, unknown>,
): Promise<WebhookResult> {
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (cfg.apiKey) headers["Authorization"] = `Bearer ${cfg.apiKey}`;
    const res = await fetch(cfg.url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      // Default mode: 'cors'. The receiver must allow this origin.
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      console.warn("webhook non-2xx:", res.status, text.slice(0, 200));
      return { ok: false, status: res.status, error: text.slice(0, 200) };
    }
    return { ok: true, status: res.status };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn("webhook fetch failed:", msg);
    return { ok: false, error: msg };
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(fr.error ?? new Error("FileReader error"));
    fr.readAsDataURL(blob);
  });
}
